import { useEffect } from 'react';
import type Konva from 'konva';
import { computeWheelZoom } from '../lib/stageInteractions';
import type { Point } from '../lib/stageInteractions';

export function useCanvasKeyboardShortcuts(input: {
  stageRef: { current: Konva.Stage | null };
  setStage: (partial: { scale?: number; offsetX?: number; offsetY?: number }) => void;

  spacePressedRef: { current: boolean };
  setPanMode: (next: boolean) => void;

  isPanning: boolean;
  setIsPanning: (next: boolean) => void;

  isConnecting: boolean;
  cancelConnectingMode: () => void;

  setShapePickerState: (updater: any) => void;
}): void {
  const {
    stageRef,
    setStage,
    spacePressedRef,
    setPanMode,
    isPanning,
    setIsPanning,
    isConnecting,
    cancelConnectingMode,
    setShapePickerState,
  } = input;

  // Keyboard support: Space toggles Pan mode; Esc cancels connection
  useEffect(() => {
    const isEditableTarget = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t) return false;
      // Space activates focused buttons (including chart layout choices).
      // Do not cancel their native click or temporarily pan the canvas.
      if (e.code === 'Space' && t.closest('button, [role="button"], [role="radio"], [role="switch"]')) return true;
      const tag = t.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return true;
      if ((t as HTMLElement).isContentEditable) return true;
      return false;
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (isEditableTarget(e)) return; // Do not hijack keys while typing
      if (e.code === 'Space' && !spacePressedRef.current) {
        e.preventDefault();
        spacePressedRef.current = true;
        setPanMode(true);
      } else if ((e.key === '+' || e.key === '=') && (e.ctrlKey || e.metaKey)) {
        // Zoom in
        e.preventDefault();
        const stage = stageRef.current;
        if (!stage) return;
        const pointer = (stage.getPointerPosition() || { x: stage.width() / 2, y: stage.height() / 2 }) as Point;
        const { scale, position } = computeWheelZoom(stage.scaleX(), pointer, { x: stage.x(), y: stage.y() }, -120);
        stage.scale({ x: scale, y: scale });
        stage.position(position);
        setStage({ scale, offsetX: position.x, offsetY: position.y });
      } else if ((e.key === '-' || e.key === '_') && (e.ctrlKey || e.metaKey)) {
        // Zoom out
        e.preventDefault();
        const stage = stageRef.current;
        if (!stage) return;
        const pointer = (stage.getPointerPosition() || { x: stage.width() / 2, y: stage.height() / 2 }) as Point;
        const { scale, position } = computeWheelZoom(stage.scaleX(), pointer, { x: stage.x(), y: stage.y() }, 120);
        stage.scale({ x: scale, y: scale });
        stage.position(position);
        setStage({ scale, offsetX: position.x, offsetY: position.y });
      } else if (e.key === '0' && (e.ctrlKey || e.metaKey)) {
        // Reset zoom
        e.preventDefault();
        const stage = stageRef.current;
        if (!stage) return;
        stage.scale({ x: 1, y: 1 });
        stage.position({ x: 0, y: 0 });
        setStage({ scale: 1, offsetX: 0, offsetY: 0 });
      } else if (e.key === 'Escape') {
        // Exit pan/view mode immediately and cancel transient operations
        e.preventDefault();
        e.stopPropagation();
        setPanMode(false);
        if (isPanning) setIsPanning(false);
        if (isConnecting) {
          cancelConnectingMode();
        }
        setShapePickerState((prev: any) =>
          prev.visible
            ? {
                visible: false,
                position: { x: 0, y: 0 },
                sourceNodeId: null,
                sourceSide: null,
                sourceRect: null,
              }
            : prev,
        );
        document.body.style.cursor = 'default';
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (isEditableTarget(e)) return; // Let inputs handle their own keys
      if (e.code === 'Space') {
        e.preventDefault();
        spacePressedRef.current = false;
        setPanMode(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown, { capture: true });
    window.addEventListener('keyup', handleKeyUp, { capture: true });

    return () => {
      window.removeEventListener('keydown', handleKeyDown, { capture: true });
      window.removeEventListener('keyup', handleKeyUp, { capture: true });
      document.body.style.cursor = 'default';
      spacePressedRef.current = false;
    };
    // Preserve existing behavior/deps from the in-component effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancelConnectingMode, isConnecting, isPanning, setPanMode]);
}
